import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { betrag, budgetText } from '../lib/geld'
import { kiBudgetQuery, kiModelleQuery, kiQuoteQuery, kiStandQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button, Card, CardPad } from './ui/primitives'

/** Die KI-Anbieter, die Work kennt – wie `KI_ANBIETER` im Server. */
type Anbieter = 'openrouter' | 'anthropic' | 'openai' | 'mistral'

/** Beispiele in den leeren Feldern – je Anbieter, sonst führt der Vorschlag in die Irre. */
const BEISPIEL: Record<Anbieter, { schluessel: string; modell: string }> = {
  openrouter: { schluessel: 'sk-or-v1-…', modell: 'google/gemini-2.5-flash-lite' },
  anthropic: { schluessel: 'sk-ant-…', modell: 'claude-haiku-4-5' },
  openai: { schluessel: 'sk-…', modell: 'gpt-4.1-mini' },
  mistral: { schluessel: 'Schlüssel aus console.mistral.ai', modell: 'mistral-small-latest' },
}

/**
 * **KI-Zugang** (Phase 6, Konzept Kap. 7, Mockup `v-ki`).
 *
 * Von Nils gewünscht (02.08.2026): *„die ki keys will ich im frontend eingeben. die können
 * sich schon mal ändern. da gehe ich auf openrouter."*
 *
 * ## Nur schreibend – und was das für die Bedienung heißt
 *
 * Das Schlüsselfeld ist **immer leer**, auch wenn einer hinterlegt ist. Es wird nicht mit
 * Sternchen vorbelegt: eine Maske mit der richtigen Anzahl Zeichen sieht wie ein
 * gespeicherter Wert aus, den man bearbeiten könnte – und verrät die Länge.
 *
 * Angezeigt werden stattdessen die **letzten vier Zeichen** und das Ergebnis des letzten
 * Verbindungstests. Vier Zeichen beantworten die einzige Frage, die man vor einem Wechsel
 * hat: *ist das noch der Schlüssel, den ich meine?* – und sind zu wenig, um einen zu
 * erraten. Dieselbe Überlegung wie beim Benutzernamen im NetCon-Zugang.
 *
 * ## Warum sofort geprüft wird
 *
 * Ein Schlüssel, der nicht trägt, soll das sagen, solange derjenige noch davorsitzt, der
 * ihn eingegeben hat – und nicht drei Tage später in einem Hintergrundjob. Der Test fragt
 * die Modell-Liste ab und kostet deshalb nichts.
 */
export function KIZugang() {
  const { data: stand, error } = useQuery(kiStandQuery)
  const { data: quote = [] } = useQuery(kiQuoteQuery)
  const { data: verbrauch } = useQuery(kiBudgetQuery)
  const [anbieter, setAnbieter] = useState<Anbieter>('openrouter')
  const [apiKey, setApiKey] = useState('')
  const [modell, setModell] = useState('')
  const [budget, setBudget] = useState('')
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const queryClient = useQueryClient()

  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['ki'] })

  const uebernehmen = (r: { ok: boolean; info?: string; fehler?: string }) => {
    setMeldung({
      art: r.ok ? 'ok' : 'fehler',
      text: r.ok ? (r.info ?? 'Zugang angenommen') : (r.fehler ?? 'Der Zugang trägt nicht.'),
    })
    /** Schlüssel aus dem Formularzustand entfernen, sobald er übergeben ist. */
    setApiKey('')
    neuLaden()
  }

  const speichern = useMutation({
    mutationFn: (v: {
      anbieter: Anbieter
      apiKey: string
      standardModell?: string
      budgetCentMonat?: number
    }) => trpc.ki.zugangSetzen.mutate(v),
    onSuccess: uebernehmen,
    onError: (f: Error) => setMeldung({ art: 'fehler', text: f.message }),
  })

  const pruefen = useMutation({
    mutationFn: (v: { anbieter: Anbieter }) => trpc.ki.pruefen.mutate(v),
    onSuccess: uebernehmen,
    onError: (f: Error) => setMeldung({ art: 'fehler', text: f.message }),
  })

  const entfernen = useMutation({
    mutationFn: (v: { anbieter: Anbieter }) => trpc.ki.zugangEntfernen.mutate(v),
    onSuccess: () => {
      setMeldung(null)
      neuLaden()
    },
  })

  if (error) return null

  return (
    <Card className="mt-3">
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-[13.5px] font-bold">KI-Zugang</h3>
          <span className="text-[11.5px] text-muted">
            Schlüssel werden verschlüsselt gespeichert und nie zurückgegeben
          </span>
        </div>

        {stand && !stand.schluesselVorhanden && (
          <p className="mt-2 text-[12px] text-pool-sofort">
            <strong>CREDENTIAL_KEY fehlt in der `.env`.</strong> Gespeicherte Schlüssel lassen sich
            damit nicht lesen – eingeben lässt sich einer, benutzen nicht.
          </p>
        )}

        {/* ── Was hinterlegt ist ──────────────────────────────── */}
        {stand && stand.zugaenge.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-[560px] mt-3 w-full border-collapse text-[12px]">
              <thead>
                <tr>
                  {['Anbieter', 'Schlüssel', 'Modell', 'Budget', 'zuletzt geprüft', ''].map((h) => (
                    <th
                      key={h}
                      className="border-b border-line px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-[0.4px] text-muted uppercase"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stand.zugaenge.map((z) => (
                  <tr key={z.anbieter}>
                    <td className="border-b border-line px-2 py-1.5">
                      <strong>{z.label}</strong>
                    </td>
                    <td className="border-b border-line px-2 py-1.5 font-mono text-[11.5px]">
                      …{z.endetAuf}
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      <ModellFeld anbieter={z.anbieter} wert={z.standardModell} fertig={neuLaden} />
                      {/* Notizbuch, 23.10.2026: zweites Modell fürs Formulieren; fällt eines aus,
                          springt das andere ein */}
                      <span className="mt-0.5 flex items-center gap-1 text-[11.5px] text-muted">
                        Denkarbeit:
                        <ModellFeld
                          anbieter={z.anbieter}
                          wert={z.starkesModell}
                          feld="stark"
                          fertig={neuLaden}
                        />
                      </span>
                    </td>
                    {/**
                     * **Hier änderbar, ohne den Schlüssel** (Nils, 03.08.2026: *„wie kann ich
                     * denn das Monatsbudget im Nachhinein setzen?"*).
                     *
                     * Vorher ging es nur über das Formular darunter – und das verlangt einen
                     * API-Schlüssel, den Work nie zurückgibt. Wer das Budget ändern wollte,
                     * musste ihn beim Anbieter neu abholen. Ein Geheimnis und eine Einstellung
                     * gehören nicht ins selbe Formular.
                     */}
                    <td className="tabular border-b border-line px-2 py-1.5">
                      <BudgetFeld
                        anbieter={z.anbieter}
                        cent={z.budgetCentMonat}
                        fertig={neuLaden}
                      />
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      {z.letztePruefungAm === null ? (
                        <span className="text-muted">nie</span>
                      ) : z.letztePruefungOk ? (
                        <span title={z.letztePruefungInfo ?? undefined}>
                          ✓ {new Date(z.letztePruefungAm).toLocaleString('de-DE')}
                        </span>
                      ) : (
                        <span className="text-pool-sofort" title={z.letzterFehler ?? undefined}>
                          ✗ {new Date(z.letztePruefungAm).toLocaleString('de-DE')}
                        </span>
                      )}
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      <div className="flex gap-1.5">
                        <Button
                          variant="ghost"
                          onClick={() => pruefen.mutate({ anbieter: z.anbieter as 'openrouter' })}
                          disabled={pruefen.isPending}
                        >
                          prüfen
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => entfernen.mutate({ anbieter: z.anbieter as 'openrouter' })}
                        >
                          entfernen
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Eingeben ────────────────────────────────────────── */}
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (apiKey.trim().length < 20) {
              setMeldung({ art: 'fehler', text: 'Das sieht nicht nach einem API-Schlüssel aus.' })
              return
            }
            const cent = budget.trim() === '' ? undefined : Math.round(Number(budget) * 100)
            speichern.mutate({
              anbieter,
              apiKey: apiKey.trim(),
              ...(modell.trim() ? { standardModell: modell.trim() } : {}),
              ...(cent !== undefined && Number.isFinite(cent) ? { budgetCentMonat: cent } : {}),
            })
          }}
        >
          <label className="text-[12px]">
            <span className="block text-muted">Anbieter</span>
            <select
              className="mt-0.5 rounded-md border border-line px-2 py-1"
              value={anbieter}
              onChange={(e) => setAnbieter(e.target.value as Anbieter)}
            >
              <option value="openrouter">OpenRouter</option>
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="mistral">Mistral (EU – nur Notizbuch)</option>
              <option value="openai">OpenAI</option>
            </select>
          </label>

          <label className="text-[12px]">
            <span className="block text-muted">API-Schlüssel</span>
            <input
              type="password"
              autoComplete="off"
              className="mt-0.5 w-[280px] rounded-md border border-line px-2 py-1 font-mono"
              placeholder={BEISPIEL[anbieter].schluessel}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </label>

          <ModellWahl anbieter={anbieter} wert={modell} setzen={setModell} />

          <label className="text-[12px]">
            <span className="block text-muted">Budget €/Monat</span>
            <input
              className="mt-0.5 w-[100px] rounded-md border border-line px-2 py-1"
              inputMode="decimal"
              placeholder="ohne"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </label>

          <Button type="submit" disabled={speichern.isPending}>
            {speichern.isPending ? 'wird geprüft …' : 'Speichern und prüfen'}
          </Button>
        </form>

        {meldung && (
          <p
            className={`mt-2 text-[12px] ${meldung.art === 'ok' ? 'text-ink2' : 'text-pool-sofort'}`}
          >
            {meldung.art === 'ok' ? '✓ ' : '✗ '}
            {meldung.text}
          </p>
        )}

        {/* ── Verbrauch und Budget (Kap. 7) ───────────────────── */}
        {verbrauch && verbrauch.staende.length > 0 && (
          <div className="mt-4 border-t border-line pt-3">
            <h4 className="text-[12.5px] font-bold">Verbrauch diesen Monat</h4>
            <p className="mt-0.5 text-[11.5px] text-muted">
              Geschätzt aus den gemeldeten Tokenzahlen – die Anbieter melden keine Beträge. Für die
              genaue Summe beim Anbieter nachsehen; hier zählt, dass die{' '}
              <strong>Grenze wirkt</strong>.
            </p>

            {verbrauch.staende.map((s) => (
              <div key={s.anbieter} className="mt-2">
                <div className="flex items-baseline gap-2 text-[12.5px]">
                  <strong>{betrag(s.verbrauchtCent)}</strong>
                  {s.budgetCent === null ? (
                    <span className="text-muted">kein Budget gesetzt – keine Grenze</span>
                  ) : (
                    <span className={s.erschoepft ? 'text-pool-sofort' : 'text-muted'}>
                      von {budgetText(s.budgetCent)}
                      {s.erschoepft && ' · erreicht, es wird nicht mehr gefragt'}
                    </span>
                  )}
                  <span className="ml-auto tabular text-[11px] text-muted">
                    {s.aufrufe} Aufrufe
                  </span>
                </div>
                {/**
                 * Der Balken erscheint nur mit Budget. Ohne Grenze gibt es keinen Anteil,
                 * und ein Balken ohne Ende wäre eine Aussage, die es nicht gibt.
                 */}
                {s.anteil !== null && (
                  <div className="mt-1 h-[5px] overflow-hidden rounded-full bg-accent-soft">
                    <i
                      className={`block h-full rounded-full ${s.erschoepft ? 'bg-pool-sofort' : 'bg-accent'}`}
                      style={{ width: `${Math.min(100, s.anteil * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            ))}

            {verbrauch.aufgeschluesselt.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-[560px] mt-2.5 w-full border-collapse text-[12px]">
                  <thead>
                    <tr>
                      {['Modell', 'wofür', 'Aufrufe', 'davon Fehler', 'Kosten'].map((h) => (
                        <th
                          key={h}
                          className="border-b border-line px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-[0.4px] text-muted uppercase"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {verbrauch.aufgeschluesselt.map((z) => (
                      <tr key={`${z.modell}-${z.funktion}`}>
                        <td className="border-b border-line px-2 py-1.5 font-mono text-[11px]">
                          {z.modell}
                        </td>
                        <td className="border-b border-line px-2 py-1.5 text-ink2">{z.funktion}</td>
                        <td className="tabular border-b border-line px-2 py-1.5">{z.aufrufe}</td>
                        {/**
                         * **Fehlversuche stehen dabei**, weil sie kosten und weil eine
                         * Funktion, die zur Hälfte scheitert, sonst billig aussieht.
                         */}
                        <td className="tabular border-b border-line px-2 py-1.5">
                          {z.fehler > 0 ? (
                            <span className="text-pool-sofort">{z.fehler}</span>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td className="tabular border-b border-line px-2 py-1.5">
                          {betrag(z.cent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-1.5 text-[11px] text-muted">
              Vorschläge entstehen seit dem 03.08.2026 von selbst: alle 20 Minuten, höchstens 15
              Aufgaben je Runde. Ist das Budget erreicht, pausiert das – der Knopf oben bleibt für
              „jetzt sofort".
            </p>
          </div>
        )}

        {/* ── Übernahmequote (Kap. 7) ─────────────────────────── */}
        {quote.length > 0 && (
          <div className="mt-4 border-t border-line pt-3">
            <h4 className="text-[12.5px] font-bold">Übernahmequote</h4>
            <p className="mt-0.5 text-[11.5px] text-muted">
              Die Steuergröße dafür, ob und wann Vorschläge automatisch übernommen werden dürfen.{' '}
              <strong>Offene zählen nicht mit</strong> – sie sind keine Zustimmung, sondern eine
              Frage, die noch niemand beantwortet hat.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-[560px] mt-2 w-full border-collapse text-[12px]">
                <thead>
                  <tr>
                    {['Modell', 'übernommen', 'geändert', 'verworfen', 'offen', 'Quote'].map(
                      (h) => (
                        <th
                          key={h}
                          className="border-b border-line px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-[0.4px] text-muted uppercase"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {quote.map((q) => (
                    <tr key={q.modell}>
                      <td className="border-b border-line px-2 py-1.5 font-mono text-[11px]">
                        {q.modell}
                      </td>
                      <td className="tabular border-b border-line px-2 py-1.5">{q.uebernommen}</td>
                      {/**
                       * **„Geändert" ist die aufschlussreichste Spalte.** Ein Vorschlag, der
                       * korrigiert wurde, war nah dran und trotzdem falsch – eine Quote ohne
                       * diese Unterscheidung sähe gut aus, obwohl jedes zweite Ergebnis von
                       * Hand nachgebessert wurde.
                       */}
                      <td className="tabular border-b border-line px-2 py-1.5">{q.geaendert}</td>
                      <td className="tabular border-b border-line px-2 py-1.5">{q.verworfen}</td>
                      <td className="tabular border-b border-line px-2 py-1.5 text-muted">
                        {q.offen}
                      </td>
                      <td className="tabular border-b border-line px-2 py-1.5">
                        {q.quote === null ? (
                          <span className="text-muted" title="noch nichts entschieden">
                            –
                          </span>
                        ) : (
                          `${Math.round(q.quote * 100)} %`
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="mt-2 text-[11px] text-muted">
          Ein hinterlegter Schlüssel wird nie zurückgegeben – auch nicht an diese Seite. Wer ihn
          nachsehen will, sieht ihn beim Anbieter nach.
        </p>
      </CardPad>
    </Card>
  )
}

/**
 * **Modellauswahl** – die verfügbaren Modelle statt eines Textfelds.
 *
 * Nils, 03.08.2026: *„dann muss ich nicht den genauen Namen immer kennen."*
 *
 * ## Warum mit Preis im Eintrag
 *
 * Die Frage bei einem Modell ist nie nur „welches", sondern immer „was kostet es". Bei 326
 * verfügbaren Modellen ist der Preis das Ordnungsmerkmal – deshalb steht er im Eintrag und
 * ist die Sortierung.
 *
 * ## Warum ein Textfeld übrig bleibt
 *
 * Ist noch kein Schlüssel hinterlegt oder antwortet der Anbieter nicht, kommt eine leere
 * Liste zurück. Dann muss man den Namen eintippen können – eine Auswahl ohne Einträge wäre
 * eine Sackgasse genau in dem Moment, in dem jemand das erste Mal etwas einrichtet.
 */
function ModellWahl({
  anbieter,
  wert,
  setzen,
  ohneBeschriftung = false,
}: {
  anbieter: string
  wert: string
  setzen: (v: string) => void
  /** In der Tabellenzeile ist die Spaltenüberschrift die Beschriftung. */
  ohneBeschriftung?: boolean
}) {
  const { data: modelle = [] } = useQuery(kiModelleQuery(anbieter))
  const empfohlen = modelle.filter((m) => m.empfehlung !== null)

  if (modelle.length === 0) {
    return (
      <label className="text-[12px]">
        {!ohneBeschriftung && <span className="block text-muted">Standardmodell (optional)</span>}
        <input
          className="mt-0.5 w-[240px] rounded-md border border-line px-2 py-1 font-mono text-[11.5px]"
          placeholder={BEISPIEL[anbieter as Anbieter]?.modell ?? ''}
          value={wert}
          onChange={(e) => setzen(e.target.value)}
        />
        <span className="mt-0.5 block text-[10.5px] text-muted">
          Erst speichern – danach erscheint hier die Liste des Anbieters
        </span>
      </label>
    )
  }

  return (
    <label className="text-[12px]">
      {!ohneBeschriftung && (
        <span className="block text-muted">Standardmodell ({modelle.length} verfügbar)</span>
      )}
      <select
        className="mt-0.5 w-[340px] rounded-md border border-line px-2 py-1 text-[11.5px]"
        value={wert}
        onChange={(e) => setzen(e.target.value)}
      >
        <option value="">– keins –</option>
        {empfohlen.length > 0 && (
          <optgroup label="Empfohlen">
            {empfohlen.map((m) => (
              <option key={m.id} value={m.id} title={m.empfehlung ?? undefined}>
                {m.id} · {preis(m.preisRein)} – {m.empfehlung}
              </option>
            ))}
          </optgroup>
        )}
        <optgroup label={`Alle, nach Preis (${modelle.length})`}>
          {modelle.map((m) => (
            <option key={m.id} value={m.id}>
              {m.id} · {preis(m.preisRein)} rein / {preis(m.preisRaus)} raus
            </option>
          ))}
        </optgroup>
      </select>
    </label>
  )
}

/** Preis je Million Token, kurz. „gratis" ist eine Aussage, „$0.00" sieht nach Fehler aus. */
function preis(wert: number | null): string {
  if (wert === null) return 'Preis unbekannt'
  if (wert === 0) return 'gratis'
  return `$${wert < 1 ? wert.toFixed(2) : wert.toFixed(2)}/M`
}

/**
 * **Das Monatsbudget in der Zeile ändern** (03.08.2026).
 *
 * Zeigt den Wert, bis jemand ihn anklickt. Ein Formularfeld, das immer offen steht, sieht
 * aus wie eine Eingabe, die noch fehlt – bei einer Einstellung, die meistens stimmt, ist das
 * die falsche Aufforderung.
 *
 * **Leer heißt „kein Budget"**, und das steht auch so da. Ein leeres Feld stillschweigend als
 * 0 zu lesen wäre die schlimmste Auslegung: null Euro Budget hieße, dass gar nichts mehr
 * läuft.
 */
function BudgetFeld({
  anbieter,
  cent,
  fertig,
}: {
  anbieter: string
  cent: number | null
  fertig: () => void
}) {
  const [offen, setOffen] = useState(false)
  const [wert, setWert] = useState(cent === null ? '' : String(cent / 100))

  const setzen = useMutation({
    mutationFn: (budgetCentMonat: number | null) =>
      trpc.ki.einstellungenSetzen.mutate({ anbieter: anbieter as 'openrouter', budgetCentMonat }),
    onSuccess: () => {
      setOffen(false)
      fertig()
    },
  })

  if (!offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="cursor-pointer rounded-md px-1 py-0.5 hover:bg-[#f3f2ef]"
        title="Monatsbudget ändern"
      >
        {cent === null ? <span className="text-muted">keins · ändern</span> : budgetText(cent)}
      </button>
    )
  }

  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault()
        const roh = wert.trim().replace(',', '.')
        if (roh === '') return setzen.mutate(null)
        const zahl = Number(roh)
        if (!Number.isFinite(zahl) || zahl < 0) return
        setzen.mutate(Math.round(zahl * 100))
      }}
    >
      <input
        autoFocus
        inputMode="decimal"
        value={wert}
        onChange={(e) => setWert(e.target.value)}
        placeholder="ohne"
        className="w-[70px] rounded-md border border-line px-1.5 py-0.5 text-[11.5px]"
      />
      <span className="text-[11px] text-muted">€</span>
      <Button type="submit" variant="ghost" size="sm" disabled={setzen.isPending}>
        ok
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setOffen(false)}>
        ✕
      </Button>
    </form>
  )
}

/** Dasselbe für das Standardmodell – aus demselben Grund. */
function ModellFeld({
  anbieter,
  wert,
  fertig,
  feld = 'standard',
}: {
  anbieter: string
  wert: string | null
  fertig: () => void
  /** `stark`: das Modell für Vorbereitung, Protokoll und Fragen (sonst das Standardmodell). */
  feld?: 'standard' | 'stark'
}) {
  const [offen, setOffen] = useState(false)
  const [gewaehlt, setGewaehlt] = useState(wert ?? '')

  const setzen = useMutation({
    mutationFn: (modell: string | null) =>
      trpc.ki.einstellungenSetzen.mutate({
        anbieter: anbieter as 'openrouter',
        ...(feld === 'stark' ? { starkesModell: modell } : { standardModell: modell }),
      }),
    onSuccess: () => {
      setOffen(false)
      fertig()
    },
  })

  if (!offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="cursor-pointer rounded-md px-1 py-0.5 text-left hover:bg-[#f3f2ef]"
        title={
          feld === 'stark'
            ? 'Modell für Vorbereitung, Protokoll und Fragen'
            : 'Standardmodell ändern'
        }
      >
        {wert ?? (
          <span className="text-muted">
            {feld === 'stark' ? 'wie Standard · wählen' : 'keins · wählen'}
          </span>
        )}
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <ModellWahl
        anbieter={anbieter as 'openrouter'}
        wert={gewaehlt}
        setzen={setGewaehlt}
        ohneBeschriftung
      />
      <Button
        variant="ghost"
        size="sm"
        disabled={setzen.isPending}
        onClick={() => setzen.mutate(gewaehlt.trim() === '' ? null : gewaehlt.trim())}
      >
        ok
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setOffen(false)}>
        ✕
      </Button>
    </div>
  )
}
